import OmSpinner from "@/components/OmSpinner";
import { T } from "@/components/T";
import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchServices } from "@/lib/api";
import { SERVICE_BY_KIND } from "@/lib/categories";
import { resolveServiceImage } from "@/lib/images";
import { MandalaMotif } from "@/components/MotifBackground";
import NumerologyCalculator from "@/components/NumerologyCalculator";
import { ArrowRight, Clock } from "lucide-react";

export default function Services() {
  const { kind } = useParams();
  const { pick, lang, isDeva } = useLang();
  const meta = SERVICE_BY_KIND[kind];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    fetchServices(kind).then(setItems).finally(() => setLoading(false));
  }, [kind, meta]);

  if (!meta) return <Navigate to="/services" replace />;
  const desc = lang === "en" ? meta.desc_en : meta.desc_hi;

  return (
    <>
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-16 -top-8 h-72 w-72 text-[var(--gold)] opacity-[0.07]" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline-label mb-3"><T>Services</T></p>
          <h1 className={`${isDeva ? "font-deva" : "font-serif-latin"} text-5xl text-ink deva-lh md:text-6xl`}>{pick(meta.deva, meta.en)}</h1>
          <p className="mt-4 max-w-2xl leading-relaxed text-ink-2">{desc}</p>
          <p className="mt-3 text-sm text-ink-3"><T>No online payment — submit a request and our team will contact you to confirm.</T></p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-16">
        {kind === "numerology" && (
          <div className="mb-14"><NumerologyCalculator /></div>
        )}
        {kind === "panchang" && (
          <div className="mb-14 flex flex-wrap items-center justify-between gap-4 border border-line bg-[var(--surface-alt)]/40 p-6">
            <div>
              <p className="font-serif-latin text-xl text-ink"><T>View today's Panchangam</T></p>
              <p className="text-sm text-ink-3"><T>Browse the daily tithi, nakshatra, yoga, karana, sunrise and sunset — free.</T></p>
            </div>
            <Link to="/panchang" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
              <T>Open Panchangam</T> <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {loading ? (
          <OmSpinner />
        ) : items.length === 0 ? (
          <p className="py-16 text-center text-ink-3"><T>No offerings listed yet.</T></p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => (
              <Link key={s.slug} to={`/service/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group flex flex-col overflow-hidden border border-line bg-surface transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]">
                {s.image_url !== null && (
                  <div className="h-40 sm:h-44 overflow-hidden">
                    <img src={resolveServiceImage(s)} alt={s.name} loading="eager" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <h3 className={`${isDeva ? "font-deva" : "font-serif-latin"} text-xl sm:text-2xl text-ink deva-lh`}>{pick(s.devanagari_name, s.name)}</h3>
                  <p className="mt-2.5 sm:mt-3 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-2 sm:line-clamp-3">{s.description}</p>
                  <div className="mt-4 sm:mt-5 flex items-center justify-between border-t border-line pt-3.5 sm:pt-4">
                    {s.duration && <span className="inline-flex items-center gap-1 text-xs text-ink-3"><Clock className="h-3.5 w-3.5" />{s.duration}</span>}
                    <span className="inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5"><T>Details & request</T> <ArrowRight className="h-4 w-4" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
